import {
  Button,
  Collapse,
  Heading,
  HStack,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Card } from "@components/ui-kit";
import { Plural, Trans, t } from "@lingui/macro";
import { OurWorkItem } from "@src/components/modules/invoices/Create/components/Steps/BudgetsSplits/ProjectCard/OurWorkItem";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, useMemo, useState } from "react";
import { ProjectModel, type Projects } from "../../../../models";
import { useInvoiceStore } from "../../../../store";
import ExpenseItem from "./ExpenseItem";
import Section from "./Section";

interface ProjectCardProps {
  project: Projects[0];
}

export const ProjectCard: FC<React.PropsWithChildren<ProjectCardProps>> =
  observer(function ProjectCard({ project: _project }) {
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
    const store = useInvoiceStore();
    const [project] = useState<ProjectModel>(
      () => new ProjectModel(_project, store),
    );

    const disableItems = useMemo(
      () => !!project.finished_at || !!project.archived_at,
      [project.finished_at, project.archived_at],
    );

    return (
      <Card key={project.id} style={{ contain: "content" }}>
        <HStack
          pos="sticky"
          zIndex="10"
          top="0"
          px="5"
          py="4"
          bg="white"
          borderColor={isOpen ? "grey.50" : "transparent"}
          borderBottomWidth="1px"
          roundedTop="xl"
          spacing="3"
        >
          <IconButton
            aria-label={t`collapse/expand projects`}
            colorScheme="grey"
            icon={
              <Icon
                name={isOpen ? "chevron-down" : "chevron-right"}
                w="4"
                h="4"
              />
            }
            onClick={onToggle}
            size="sm"
            variant="outline"
          />
          <HStack align="baseline" justify="space-between" flex="1">
            <HStack align="baseline" spacing="2">
              <Heading size="xs">{`${project.code} - ${project.title}`}</Heading>
              {project.selectedCount && (
                <Text color="grey.500" fontSize="xs">
                  <Plural
                    value={project.selectedCount}
                    one="(# item selected)"
                    other="(# items selected)"
                  />
                </Text>
              )}
            </HStack>
            {project.itemsLength !== 0 && (
              <Button
                onClick={project.toggleSelectAll}
                size="sm"
                variant="link"
              >
                {project.allSelected ? (
                  <Trans>Deselect all</Trans>
                ) : (
                  <Trans>Select all</Trans>
                )}
              </Button>
            )}
          </HStack>
        </HStack>
        <Collapse animateOpacity in={isOpen}>
          {project.ourWorkStructured.map((ourWork) => (
            <Section
              key={ourWork.id}
              title={ourWork.name}
              hide={ourWork.budgetItems.length === 0}
              item={ourWork}
            >
              {ourWork.budgetItems.map((budgetItem) => (
                <OurWorkItem
                  key={budgetItem.id}
                  item={budgetItem}
                  disabled={disableItems}
                  budgetCategoryId={ourWork.budget_category_id}
                />
              ))}
            </Section>
          ))}
          {project.expensesWithoutOtherStructured.map((category) => (
            <Section
              key={category.id}
              title={category.name}
              hide={category.budgetItems.length === 0}
              item={category}
            >
              {category.budgetItems.map((budgetItem) => (
                <ExpenseItem
                  key={budgetItem.id}
                  item={budgetItem}
                  disabled={disableItems}
                  budgetCategoryId={category.budget_category_id}
                />
              ))}
            </Section>
          ))}
        </Collapse>
      </Card>
    );
  });
